/**
 * TreatX Admin - Provider Validation Panel (P0 FIX)
 * P2.3b: Full i18n coverage
 * 
 * Fixes:
 * - Working Approve/Reject with validity period
 * - Certifications as part of Provider Detail (not standalone)
 * - Clear provider context loading on selection
 * - Full detail drawer with all info
 */
import React, { useState, useEffect } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  useProviderOnboarding, 
  PROVIDER_TYPES, 
  SUBSCRIPTION_PACKAGES,
  ONBOARDING_STATUS,
  REQUIRED_DOCUMENTS
} from '../../context/ProviderOnboardingContext';
import { useBoardCertification, CERTIFICATION_STATUS } from '../../context/BoardCertificationContext';
import { getLocalizedField } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Shield, CheckCircle, XCircle, Clock, FileText, MapPin, Building2,
  User, CreditCard, Calendar, Eye, Download, Search, Filter,
  ChevronRight, AlertTriangle, Check, X, Loader2, RefreshCw,
  Star, Award, FileCheck, Globe, Phone, Mail, ChevronLeft, Lock,
  Info, ArrowRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import DateField from '../../components/DateField';
import { api } from '../../utils/api';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../../components/ui/dialog';

// Provider Detail Drawer - Full Context View
const ProviderDetailDrawer = ({ 
  provider, 
  onClose, 
  onApprove, 
  onReject,
  onSuspend,
  onReinstate,
  certifications,
  onApproveCertification,
  onRejectCertification
}) => {
  const { t } = useTranslation();
  const [validityMonths, setValidityMonths] = useState(12);
  const [customValidUntil, setCustomValidUntil] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [suspensionReason, setSuspensionReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  if (!provider) return null;

  const providerType = PROVIDER_TYPES[provider.providerType] || PROVIDER_TYPES[provider.type];
  // Canonical pending bucket — aligned with getPendingApplications() and the
  // backend provider_applications statuses (`submitted`, `under_review`,
  // `info_requested`, `resubmitted`). Previously this only matched
  // PENDING_ADMIN_REVIEW / 'PENDING', so the Approve/Reject action footer
  // never rendered for real applications. (Phase B RCA-1, 2026-05-20)
  const pendingStatuses = new Set([
    ONBOARDING_STATUS.PENDING_ADMIN_REVIEW,
    'PENDING',
    'pending',
    'submitted',
    'under_review',
    'info_requested',
    'resubmitted',
  ]);
  const isPending = pendingStatuses.has(provider.status);
  const isApproved = provider.status === ONBOARDING_STATUS.APPROVED || provider.status === 'APPROVED';
  const isSuspended = provider.status === ONBOARDING_STATUS.SUSPENDED || provider.status === 'SUSPENDED';

  // P3-5 L3: Determine package status
  const getPackageStatusBadge = () => {
    if (!provider.package) {
      return <Badge className="bg-gray-100 text-gray-600">Package: Not Selected</Badge>;
    }
    if (provider.payment?.status !== 'completed') {
      return <Badge className="bg-amber-100 text-amber-700">Package: Payment Pending</Badge>;
    }
    if (provider.validUntil && new Date(provider.validUntil) < new Date()) {
      return <Badge className="bg-red-100 text-red-700">Package: Expired</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Package: Active</Badge>;
  };

  // Filter certifications for this provider
  const providerCertifications = certifications?.filter(c => 
    c.providerId === provider.id || c.organizationId === provider.id
  ) || [];

  const handleApprove = async () => {
    setLoading(true);
    let validUntil;
    
    if (validityMonths === 'custom') {
      if (!customValidUntil) {
        toast({ title: 'Date Required', description: 'Please select a custom validity date', variant: 'destructive' });
        setLoading(false);
        return;
      }
      validUntil = customValidUntil;
    } else {
      validUntil = new Date();
      validUntil.setMonth(validUntil.getMonth() + parseInt(validityMonths));
      validUntil = validUntil.toISOString().split('T')[0];
    }
    
    await onApprove(provider.id, validUntil, internalNotes);
    setLoading(false);
    toast({ title: 'Provider Approved', description: `${provider.legalInfo?.legalName || provider.legalInfo?.fullName} has been approved.` });
    onClose();
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({ title: 'Reason Required', description: 'Please provide a rejection reason', variant: 'destructive' });
      return;
    }
    setLoading(true);
    await onReject(provider.id, rejectionReason);
    setLoading(false);
    toast({ title: 'Provider Rejected', description: 'The provider has been notified.' });
    onClose();
  };

  // P3-5 L4: Handle suspend - FIXED: Now properly triggers state update and drawer refresh
  const handleSuspend = async () => {
    if (!suspensionReason.trim()) {
      toast({ title: 'Reason Required', description: 'Please provide a suspension reason', variant: 'destructive' });
      return;
    }
    setLoading(true);
    // Call suspend and wait for completion
    onSuspend(provider.id, suspensionReason);
    setLoading(false);
    toast({ title: 'Provider Suspended', description: 'The provider has been suspended and notified.' });
    setShowSuspendForm(false);
    // Close drawer to force refresh of provider lists
    onClose();
  };

  // P3-5 L4: Handle reinstate
  const handleReinstate = async () => {
    setLoading(true);
    const newValidUntil = new Date();
    newValidUntil.setMonth(newValidUntil.getMonth() + 12);
    await onReinstate(provider.id, newValidUntil.toISOString().split('T')[0]);
    setLoading(false);
    toast({ title: 'Provider Reinstated', description: 'The provider has been reinstated and can now receive leads.' });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex">
      {/* Drawer */}
      <div className="ml-auto w-full max-w-3xl bg-white h-full overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-700 to-pink-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-purple-200 hover:text-white hover:bg-white/20">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to List
            </Button>
            <div className="flex items-center gap-2">
              {getPackageStatusBadge()}
              <Badge className={isPending ? 'bg-amber-500' : isApproved ? 'bg-green-500' : isSuspended ? 'bg-red-500' : 'bg-gray-500'}>
                {isPending ? 'Pending Review' : isApproved ? 'Approved' : isSuspended ? 'SUSPENDED' : provider.status}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-5xl">{providerType?.icon || '🏥'}</div>
            <div>
              <h2 className="text-2xl font-bold">
                {provider.legalInfo?.legalName || provider.legalInfo?.fullName || provider.legalInfo?.tradeName}
              </h2>
              <p className="text-purple-200">
                {providerType?.name} • Ref: {provider.referenceNumber || provider.refId || provider.id.slice(0, 8)}
              </p>
              {provider.geolocation && (
                <p className="text-purple-200 text-sm flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {provider.geolocation.city}, {provider.geolocation.country}
                </p>
              )}
            </div>
          </div>
          {/* P3-5 L4: Suspended notice */}
          {isSuspended && (
            <div className="mt-4 p-3 bg-red-100/20 rounded-lg border border-red-300/30">
              <div className="flex items-center gap-2 text-red-100">
                <Lock className="h-4 w-4" />
                <span className="font-semibold">SUSPENDED</span>
              </div>
              <p className="text-sm text-red-200 mt-1">
                Reason: {provider.suspensionReason || 'Payment failed or validity expired'}
              </p>
              <p className="text-xs text-red-300 mt-1">
                Suspended on: {provider.suspendedAt ? formatDateLocale(provider.suspendedAt) : 'N/A'}
              </p>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex-1 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
            <div className="border-b px-6 pt-4">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="legal">Legal Info</TabsTrigger>
                <TabsTrigger value="location">Location</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
                <TabsTrigger value="certifications">
                  Certifications
                  {providerCertifications.length > 0 && (
                    <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">{providerCertifications.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-gray-500 uppercase">Provider Type</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-2xl">{providerType?.icon}</span>
                        <span className="font-semibold">{providerType?.name}</span>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-gray-500 uppercase">Contact Email</p>
                      <p className="font-medium mt-1">{provider.legalInfo?.email || '-'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-gray-500 uppercase">Phone</p>
                      <p className="font-medium mt-1">{provider.legalInfo?.phone || '-'}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-xs text-gray-500 uppercase">Submitted</p>
                      <p className="font-medium mt-1">
                        {provider.submittedAt ? formatDateLocale(provider.submittedAt) : 
                         provider.createdAt ? formatDateLocale(provider.createdAt) : '-'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {isApproved && (
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-semibold">Approved Provider</span>
                      </div>
                      <p className="text-sm text-green-600 mt-1">
                        Valid until: {provider.validUntil || provider.approvalValidUntil || '-'}
                      </p>
                      {provider.isLegacy && (
                        <Badge className="mt-2 bg-gray-100 text-gray-600">Legacy Provider</Badge>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Legal Info Tab */}
              <TabsContent value="legal" className="mt-0 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Shield className="h-5 w-5 text-blue-600" />
                      Legal Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="grid grid-cols-2 gap-4">
                      {Object.entries(provider.legalInfo || {}).map(([key, value]) => (
                        <div key={key} className="bg-gray-50 p-3 rounded-lg">
                          <dt className="text-xs text-gray-500 uppercase">{key.replace(/([A-Z])/g, ' $1').trim()}</dt>
                          <dd className="font-medium mt-1">{value || '-'}</dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Location Tab */}
              <TabsContent value="location" className="mt-0 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-blue-600" />
                      Geolocation Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {provider.geolocation ? (
                      <div className="space-y-4">
                        <div className="bg-gradient-to-br from-blue-100 to-green-100 rounded-lg h-40 flex items-center justify-center">
                          <div className="text-center">
                            <MapPin className="h-8 w-8 text-red-500 mx-auto mb-2" />
                            <p className="font-medium">{provider.geolocation.city}, {provider.geolocation.country}</p>
                            <p className="text-xs text-gray-500">
                              {provider.geolocation.latitude?.toFixed(4)}, {provider.geolocation.longitude?.toFixed(4)}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-500">Address</p>
                            <p className="font-medium mt-1">{provider.geolocation.address || '-'}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-500">District</p>
                            <p className="font-medium mt-1">{provider.geolocation.district || '-'}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-500">Latitude</p>
                            <p className="font-mono mt-1">{provider.geolocation.latitude?.toFixed(6) || provider.geolocation.lat?.toFixed(6)}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-500">Longitude</p>
                            <p className="font-mono mt-1">{provider.geolocation.longitude?.toFixed(6) || provider.geolocation.lng?.toFixed(6)}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500">No geolocation data available</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents" className="mt-0 space-y-4">
                {/* 3C — Review Context: onboarding documents are reviewed as
                    part of Initial Provider Verification (distinct from a
                    later Provider Identity Change). Presentation only. */}
                <div
                  className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2"
                  data-testid="review-context-onboarding"
                >
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">{t('treatxAdmin.identityAmendments.reviewContext')}:</span>{' '}
                    {t('treatxAdmin.identityAmendments.initialVerification')}
                  </p>
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Uploaded Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(provider.documents || {}).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(provider.documents || {}).map(([docId, doc]) => (
                          <div key={docId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileCheck className="h-5 w-5 text-green-500" />
                              <div>
                                <p className="font-medium">{docId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                                <p className="text-xs text-gray-500">{doc.name} • {(doc.size / 1024).toFixed(1)} KB</p>
                              </div>
                            </div>
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4 mr-1" /> View
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No documents uploaded</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Certifications Tab - Now per provider */}
              <TabsContent value="certifications" className="mt-0 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Award className="h-5 w-5 text-amber-600" />
                      Provider Certifications
                    </CardTitle>
                    <CardDescription>
                      Board certifications and credentials for this provider
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {providerCertifications.length > 0 ? (
                      <div className="space-y-3">
                        {providerCertifications.map(cert => (
                          <div key={cert.id} className="p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Award className="h-5 w-5 text-amber-500" />
                                  <p className="font-semibold">{getLocalizedField(cert.certificationName, 'en')}</p>
                                </div>
                                <p className="text-sm text-gray-600 mt-1">{cert.issuingAuthority}</p>
                                <p className="text-xs text-gray-500 mt-1">Specialty: {cert.specialty}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge className={
                                    cert.status === CERTIFICATION_STATUS.VERIFIED ? 'bg-green-100 text-green-700' :
                                    cert.status === CERTIFICATION_STATUS.PENDING ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }>
                                    {cert.status}
                                  </Badge>
                                  {cert.validUntil && (
                                    <span className="text-xs text-gray-500">Valid until: {cert.validUntil}</span>
                                  )}
                                </div>
                              </div>
                              {cert.status === CERTIFICATION_STATUS.PENDING && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => onRejectCertification(cert.id, 'Invalid documentation')}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-green-600"
                                    onClick={() => onApproveCertification(cert.id)}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Award className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>No certifications submitted for this provider</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Action Footer - For pending providers */}
        {isPending && (
          <div className="border-t p-4 bg-gray-50 flex-shrink-0">
            {showRejectForm ? (
              <div className="space-y-3">
                <Label>Rejection Reason *</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(iso) => setRejectionReason(iso)}
                  placeholder="Provide a clear reason for rejection..."
                  rows={3}
                />
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowRejectForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleReject} variant="destructive" className="flex-1" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <XCircle className="h-4 w-4 mr-2" />}
                    Confirm Rejection
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* P3-5 L2: Flexible Validity Period Selection */}
                <div className="space-y-2">
                  <Label className="whitespace-nowrap font-semibold">Validity Period: *</Label>
                  <select
                    className="w-full border rounded-lg p-2"
                    value={validityMonths}
                    onChange={(iso) => setValidityMonths(iso)}
                  >
                    <option value={1}>1 month (Trial)</option>
                    <option value={3}>3 months (Short-term)</option>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months (Standard)</option>
                    <option value={18}>18 months</option>
                    <option value={24}>24 months</option>
                    <option value="custom">Custom date range</option>
                  </select>
                  {validityMonths === 'custom' && (
                    <DateField
                      value={customValidUntil}
                      onChange={(iso) => setCustomValidUntil(iso)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-2"
                    />
                  )}
                  <p className="text-xs text-gray-500">Select the approval validity period for this provider</p>
                </div>

                {/* Internal Notes */}
                <div>
                  <Label className="text-sm text-gray-600">Internal Notes (TreatX only)</Label>
                  <Textarea
                    value={internalNotes}
                    onChange={(iso) => setInternalNotes(iso)}
                    placeholder="Add internal notes about this provider..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowRejectForm(true)} 
                    className="flex-1 text-red-600 hover:bg-red-50 border-red-200"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Application
                  </Button>
                  <Button 
                    onClick={handleApprove} 
                    className="flex-1 bg-green-600 hover:bg-green-700" 
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Approve ({validityMonths === 'custom' ? 'Custom' : `${validityMonths} months`})
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* P3-5 L4: Action Footer for Approved providers (Suspend option) */}
        {isApproved && !isSuspended && (
          <div className="border-t p-4 bg-gray-50 flex-shrink-0">
            {showSuspendForm ? (
              <div className="space-y-3">
                <Label>Suspension Reason *</Label>
                <Textarea
                  value={suspensionReason}
                  onChange={(iso) => setSuspensionReason(iso)}
                  placeholder="Reason for suspension (e.g., payment failed, validity expired)..."
                  rows={3}
                />
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowSuspendForm(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={handleSuspend} variant="destructive" className="flex-1" disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
                    Confirm Suspension
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowSuspendForm(true)} 
                  className="flex-1 text-amber-600 hover:bg-amber-50 border-amber-200"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Suspend Provider
                </Button>
                <Button variant="outline" onClick={onClose} className="flex-1">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to List
                </Button>
              </div>
            )}
          </div>
        )}

        {/* P3-5 L4: Action Footer for Suspended providers (Reinstate option) */}
        {isSuspended && (
          <div className="border-t p-4 bg-gray-50 flex-shrink-0">
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Reinstatement:</strong> Provider will be approved for 12 months and can receive new leads.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to List
                </Button>
                <Button onClick={handleReinstate} className="flex-1 bg-green-600 hover:bg-green-700" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Reinstate Provider
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===========================================================================
// 3C — Provider Identity Change review dialog.
// Reuses the existing verification reviewer look/actions (approve + reject with
// Admin-entered reason). Approval/rejection go through the canonical 3A Admin
// authority endpoints; this surface NEVER writes clinic_profiles identity
// directly. Documents are NOT part of the 3A amendment model, so no document
// review is wired here (see report — evidence linkage is intentionally absent).
// ===========================================================================
const ROW = ({ label, current, requested, changed }) => {
  const { t } = useTranslation();
  if (!changed) return null;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="identity-change-field-row">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex-1">
          <p className="text-[10px] text-slate-400 uppercase tracking-wide">{t('treatxAdmin.identityAmendments.currentApproved')}</p>
          <p className="text-sm text-slate-700 line-through decoration-slate-300">{current || '—'}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400 hidden sm:block flex-shrink-0" />
        <div className="flex-1">
          <p className="text-[10px] text-emerald-600 uppercase tracking-wide">{t('treatxAdmin.identityAmendments.requested')}</p>
          <p className="text-sm font-semibold text-emerald-700" data-testid="identity-change-requested-value">{requested}</p>
        </div>
      </div>
    </div>
  );
};

const IdentityAmendmentReviewDialog = ({ amendment, open, onOpenChange, onReviewed }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // null | 'approve' | 'reject'
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [docMode, setDocMode] = useState(null); // null | 'reject'
  const [docReason, setDocReason] = useState('');
  const [docStatusOverride, setDocStatusOverride] = useState(null);

  useEffect(() => {
    if (open) { setMode(null); setReason(''); setLoading(false); setDocMode(null); setDocReason(''); setDocStatusOverride(null); }
  }, [open, amendment?.id]);

  if (!amendment) return null;

  const brandChanged = !!amendment.proposed_brand_name &&
    amendment.proposed_brand_name !== amendment.current_brand_name;
  const legalChanged = !!amendment.proposed_invoice_legal_entity_name &&
    amendment.proposed_invoice_legal_entity_name !== amendment.current_invoice_legal_entity_name;
  const providerName = amendment.clinic_brand_name || amendment.current_brand_name
    || amendment.clinic_legal_entity_name || amendment.clinic_id;

  // 3D — supporting documents linked to this amendment.
  const documents = Array.isArray(amendment.documents) ? amendment.documents : [];
  const currentDoc = amendment.current_document
    || documents.find((d) => !d.is_superseded) || null;
  const hasDoc = !!currentDoc;
  const docApproved = !!(amendment.current_document_approved
    || (currentDoc && currentDoc.status === 'approved'));
  const approveChangeBlocked = hasDoc && !docApproved;

  const effectiveDocStatus = docStatusOverride || (currentDoc && currentDoc.status) || null;
  const docApprovedEff = effectiveDocStatus === 'approved' || docApproved;
  const approveChangeBlockedEff = hasDoc && !docApprovedEff;

  const docStatusLabel = (s) => {
    const k = { uploaded: 'pendingReview', pending_review: 'pendingReview', pending: 'pendingReview', approved: 'approved', rejected: 'rejected', archived: 'archived' }[s] || 'pendingReview';
    return t(`treatxAdmin.identityAmendments.docStatus.${k}`);
  };
  const openDoc = async (documentId, fileName) => {
    try {
      const res = await api.get(`/admin/documents/${documentId}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const w = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = fileName || 'document'; a.click(); }
    } catch (e) {
      toast({ title: t('treatxAdmin.identityAmendments.errorAction', { message: e?.response?.data?.detail || e.message }), variant: 'destructive' });
    }
  };
  const reviewDoc = async (documentId, status, comment) => {
    setLoading(true);
    try {
      await api.put(`/admin/documents/${documentId}/review`, { status, comment: comment || '' });
      toast({ title: status === 'approved' ? t('treatxAdmin.identityAmendments.toastDocApproved') : t('treatxAdmin.identityAmendments.toastDocRejected') });
      setDocMode(null); setDocReason('');
      setDocStatusOverride(status);
      onReviewed && onReviewed();
    } catch (e) {
      toast({ title: t('treatxAdmin.identityAmendments.errorAction', { message: e?.response?.data?.detail || e.message }), variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/identity-amendments/${amendment.id}/approve`);
      toast({ title: t('treatxAdmin.identityAmendments.toastApproved') });
      onOpenChange(false);
      onReviewed && onReviewed();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || '';
      if (status === 409 && String(detail).includes('SUPPORTING_DOCUMENT'))
        toast({ title: t('treatxAdmin.identityAmendments.approveChangeDisabledHint'), variant: 'destructive' });
      else if (status === 409) toast({ title: t('treatxAdmin.identityAmendments.alreadyReviewed'), variant: 'destructive' });
      else toast({ title: t('treatxAdmin.identityAmendments.errorAction', { message: detail || e.message }), variant: 'destructive' });
      onReviewed && onReviewed();
    } finally { setLoading(false); }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast({ title: t('treatxAdmin.identityAmendments.reasonRequired'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await api.put(`/admin/identity-amendments/${amendment.id}/reject`, { reason: reason.trim() });
      toast({ title: t('treatxAdmin.identityAmendments.toastRejected') });
      onOpenChange(false);
      onReviewed && onReviewed();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) toast({ title: t('treatxAdmin.identityAmendments.alreadyReviewed'), variant: 'destructive' });
      else toast({ title: t('treatxAdmin.identityAmendments.errorAction', { message: e?.response?.data?.detail || e.message }), variant: 'destructive' });
      onReviewed && onReviewed();
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid="identity-amendment-review-dialog">
        <DialogHeader>
          <DialogTitle data-testid="identity-amendment-review-title">
            {t('treatxAdmin.identityAmendments.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 text-purple-700 px-2.5 py-0.5 text-[11px] font-semibold mt-1" data-testid="review-context-amendment">
              <Info className="h-3 w-3" />
              {t('treatxAdmin.identityAmendments.reviewContext')}: {t('treatxAdmin.identityAmendments.identityChange')}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-500" />
              <span className="text-slate-500">{t('treatxAdmin.identityAmendments.provider')}:</span>
              <span className="font-medium text-slate-800" data-testid="identity-amendment-provider-name">{providerName}</span>
            </div>
            {amendment.requested_at && (
              <span className="text-[11px] text-slate-400">
                {t('treatxAdmin.identityAmendments.requestedOn')} {formatDateLocale(amendment.requested_at)}
              </span>
            )}
          </div>

          <ROW label={t('treatxAdmin.identityAmendments.publicBrand')} current={amendment.current_brand_name} requested={amendment.proposed_brand_name} changed={brandChanged} />
          <ROW label={t('treatxAdmin.identityAmendments.legalName')} current={amendment.current_invoice_legal_entity_name} requested={amendment.proposed_invoice_legal_entity_name} changed={legalChanged} />

          {/* 3D — Supporting verification documents linked to this amendment */}
          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="identity-amendment-documents">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {t('treatxAdmin.identityAmendments.supportingVerification')}
            </p>
            {!hasDoc ? (
              <p className="text-[12px] text-slate-400" data-testid="identity-amendment-no-documents">
                {t('treatxAdmin.identityAmendments.noDocuments')}
              </p>
            ) : (
              <div className="space-y-2" data-testid="identity-amendment-current-document">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{currentDoc.document_type_name || currentDoc.document_type_id}</p>
                    <p className="text-[11px] text-slate-500 truncate">{currentDoc.file_name}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${effectiveDocStatus === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : effectiveDocStatus === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`} data-testid="identity-amendment-doc-status">
                      {docStatusLabel(effectiveDocStatus)}
                    </span>
                    <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openDoc(currentDoc.document_id, currentDoc.file_name)} data-testid="identity-amendment-doc-open">
                      {t('treatxAdmin.identityAmendments.openDocument')}
                    </Button>
                  </div>
                </div>
                {docMode !== 'reject' && effectiveDocStatus !== 'approved' && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700" disabled={loading} onClick={() => reviewDoc(currentDoc.document_id, 'approved', '')} data-testid="identity-amendment-doc-approve-btn">
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> {t('treatxAdmin.identityAmendments.approveDocument')}
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200" disabled={loading} onClick={() => setDocMode('reject')} data-testid="identity-amendment-doc-reject-btn">
                      <XCircle className="h-3.5 w-3.5 mr-1" /> {t('treatxAdmin.identityAmendments.rejectDocument')}
                    </Button>
                  </div>
                )}
                {docMode === 'reject' && (
                  <div className="space-y-2" data-testid="identity-amendment-doc-reject-form">
                    <Textarea
                      value={docReason}
                      onChange={(e) => setDocReason(e.target.value)}
                      placeholder={t('treatxAdmin.identityAmendments.docReasonPlaceholder')}
                      rows={2}
                      data-testid="identity-amendment-doc-reason-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={loading} onClick={() => { setDocMode(null); setDocReason(''); }} data-testid="identity-amendment-doc-reject-cancel">
                        {t('treatxAdmin.identityAmendments.cancel')}
                      </Button>
                      <Button size="sm" variant="destructive" className="h-7 text-[11px]" disabled={loading} onClick={() => { if (!docReason.trim()) { toast({ title: t('treatxAdmin.identityAmendments.docReasonRequired'), variant: 'destructive' }); return; } reviewDoc(currentDoc.document_id, 'rejected', docReason.trim()); }} data-testid="identity-amendment-doc-reject-confirm-btn">
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                        {t('treatxAdmin.identityAmendments.rejectDocument')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {mode === 'approve' && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="identity-amendment-approve-confirm">
              <p className="text-sm font-semibold text-emerald-800">{t('treatxAdmin.identityAmendments.confirmApproveTitle')}</p>
              <p className="text-[12px] text-emerald-700">{t('treatxAdmin.identityAmendments.confirmApproveDesc')}</p>
              {brandChanged && (
                <p className="text-[12px] text-emerald-800">
                  {t('treatxAdmin.identityAmendments.publicBrand')}: <span className="font-medium">{amendment.current_brand_name || '—'}</span> → <span className="font-semibold">{amendment.proposed_brand_name}</span>
                </p>
              )}
              {legalChanged && (
                <p className="text-[12px] text-emerald-800">
                  {t('treatxAdmin.identityAmendments.legalName')}: <span className="font-medium">{amendment.current_invoice_legal_entity_name || '—'}</span> → <span className="font-semibold">{amendment.proposed_invoice_legal_entity_name}</span>
                </p>
              )}
            </div>
          )}

          {mode === 'reject' && (
            <div className="space-y-2" data-testid="identity-amendment-reject-form">
              <Label>{t('treatxAdmin.identityAmendments.reason')} *</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('treatxAdmin.identityAmendments.reasonPlaceholder')}
                rows={3}
                data-testid="identity-amendment-reason-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {mode === null && (
            <div className="flex flex-col items-end gap-1.5 w-full">
              {approveChangeBlockedEff && (
                <p className="text-[11px] text-amber-600 w-full text-left" data-testid="identity-amendment-approve-disabled-hint">
                  {t('treatxAdmin.identityAmendments.approveChangeDisabledHint')}
                </p>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="text-red-600 border-red-200" onClick={() => setMode('reject')} data-testid="identity-amendment-reject-btn">
                  <XCircle className="h-4 w-4 mr-1.5" /> {t('treatxAdmin.identityAmendments.rejectChange')}
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={approveChangeBlockedEff} onClick={() => setMode('approve')} data-testid="identity-amendment-approve-btn">
                  <CheckCircle className="h-4 w-4 mr-1.5" /> {t('treatxAdmin.identityAmendments.approveChange')}
                </Button>
              </div>
            </div>
          )}
          {mode === 'approve' && (
            <>
              <Button variant="outline" onClick={() => setMode(null)} disabled={loading} data-testid="identity-amendment-approve-back">
                {t('treatxAdmin.identityAmendments.cancel')}
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} disabled={loading} data-testid="identity-amendment-approve-confirm-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle className="h-4 w-4 mr-1.5" />}
                {t('treatxAdmin.identityAmendments.confirmApprove')}
              </Button>
            </>
          )}
          {mode === 'reject' && (
            <>
              <Button variant="outline" onClick={() => setMode(null)} disabled={loading} data-testid="identity-amendment-reject-back">
                {t('treatxAdmin.identityAmendments.cancel')}
              </Button>
              <Button variant="destructive" onClick={handleReject} disabled={loading} data-testid="identity-amendment-reject-confirm-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />}
                {t('treatxAdmin.identityAmendments.confirmReject')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Main Component
const ProviderValidationPanel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    pendingApplications,
    providers,
    getPendingApplications, 
    getApprovedProviders,
    getSuspendedProviders,
    approveApplication, 
    rejectApplication,
    suspendProvider,
    reinstateProvider,
    refreshApplications,
    refreshProviders,
  } = useProviderOnboarding();
  const { 
    certifications,
    getPendingCertifications, 
    verifyCertification, 
    rejectCertification 
  } = useBoardCertification();

  const [activeTab, setActiveTab] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);

  // 3C — Provider Identity Change amendments (admin review).
  const [amendments, setAmendments] = useState([]);
  const [selectedAmendment, setSelectedAmendment] = useState(null);
  const fetchAmendments = React.useCallback(async () => {
    try {
      const res = await api.get('/admin/identity-amendments', { params: { status: 'pending' } });
      setAmendments(Array.isArray(res?.data?.amendments) ? res.data.amendments : []);
    } catch (e) {
      setAmendments([]);
    }
  }, []);

  // Always fetch fresh data when panel mounts
  useEffect(() => {
    if (refreshApplications) refreshApplications();
    if (refreshProviders) refreshProviders();
  }, [refreshApplications, refreshProviders]);
  useEffect(() => { fetchAmendments(); }, [fetchAmendments]);

  // Get data
  const pending = getPendingApplications();
  const approved = getApprovedProviders();
  const suspended = getSuspendedProviders ? getSuspendedProviders() : [];
  const allCertifications = certifications || [];

  // Filter by search
  const filteredPending = pending.filter(app => {
    const name = app.legalInfo?.legalName || app.legalInfo?.fullName || app.clinicName || app.contactName || '';
    const ref = app.referenceNumber || app.applicationId || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           ref.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredApproved = approved.filter(p => {
    const name = p.legalInfo?.legalName || p.legalInfo?.fullName || p.legalInfo?.tradeName || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredSuspended = suspended.filter(p => {
    const name = p.legalInfo?.legalName || p.legalInfo?.fullName || p.legalInfo?.tradeName || '';
    return name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
  };

  const handleApproveCertification = (certId) => {
    verifyCertification(certId, 'TreatX Admin');
    toast({ title: 'Certification Verified', description: 'The certification has been approved.' });
  };

  const handleRejectCertification = (certId, reason) => {
    rejectCertification(certId, reason, 'TreatX Admin');
    toast({ title: 'Certification Rejected', description: 'The certification has been rejected.' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-800 to-pink-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/treatx-admin/dashboard')} className="text-purple-200 hover:text-white">
                ← Back to Dashboard
              </Button>
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold">Provider Validation Panel</h1>
              <p className="text-purple-200 text-sm">Review and approve provider applications</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-500">{pending.length} Pending</Badge>
              <Badge className="bg-green-500">{approved.length} Approved</Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between mb-6">
            <TabsList>
              <TabsTrigger value="pending" className="gap-2">
                <Clock className="h-4 w-4" />
                Pending ({pending.length})
              </TabsTrigger>
              <TabsTrigger value="approved" className="gap-2">
                <CheckCircle className="h-4 w-4" />
                Approved ({approved.length})
              </TabsTrigger>
              <TabsTrigger value="suspended" className="gap-2">
                <Lock className="h-4 w-4" />
                Suspended ({suspended.length})
              </TabsTrigger>
              <TabsTrigger value="identity" className="gap-2" data-testid="tab-identity-changes">
                <Info className="h-4 w-4" />
                {t('treatxAdmin.identityAmendments.tabTitle')} ({amendments.length})
              </TabsTrigger>
            </TabsList>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search providers..."
                value={searchQuery}
                onChange={(iso) => setSearchQuery(iso)}
                className="pl-10 w-64"
              />
            </div>
          </div>

          {/* Pending Applications */}
          <TabsContent value="pending">
            {filteredPending.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600">All Caught Up!</h3>
                  <p className="text-gray-500">No pending applications to review</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Selection Instruction */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-700">
                    Click on a provider to open the detail view and review their application.
                  </p>
                </div>

                {filteredPending.map(app => {
                  const providerType = PROVIDER_TYPES[app.providerType];
                  return (
                    <Card 
                      key={app.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-purple-300"
                      onClick={() => handleSelectProvider(app)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-4xl">{providerType?.icon || '🏥'}</div>
                            <div>
                              <h3 className="font-semibold text-lg">
                                {app.legalInfo?.legalName || app.legalInfo?.fullName || app.clinicName || app.contactName || 'Unnamed Provider'}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {providerType?.name || 'Clinic'} • {app.geolocation?.city || app.city || '-'}, {app.geolocation?.country || app.country || '-'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {app.referenceNumber || app.applicationId || 'Pending Ref'}
                                </Badge>
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  <Clock className="h-3 w-3 mr-1" /> Awaiting Review
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">Submitted</p>
                              <p className="font-medium">
                                {app.submittedAt ? formatDateLocale(app.submittedAt) : '-'}
                              </p>
                            </div>
                            <Button variant="outline">
                              Review <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Approved Providers */}
          <TabsContent value="approved">
            <div className="space-y-4">
              {filteredApproved.map(provider => {
                const providerType = PROVIDER_TYPES[provider.type] || PROVIDER_TYPES[provider.providerType];
                const pkg = SUBSCRIPTION_PACKAGES[provider.package];
                return (
                  <Card 
                    key={provider.id} 
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleSelectProvider(provider)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-4xl">{providerType?.icon || '🏥'}</div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">
                                {provider.legalInfo?.legalName || provider.legalInfo?.tradeName}
                              </h3>
                              {provider.isLegacy && (
                                <Badge className="bg-gray-100 text-gray-600 text-xs">Legacy</Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">
                              {providerType?.name} • {provider.geolocation?.city}, {provider.geolocation?.country}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              {pkg && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">{getLocalizedField(pkg.name, 'en')}</Badge>
                              )}
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approved
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Valid Until</p>
                          <p className="font-medium">{provider.validUntil || provider.approvalValidUntil}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* P3-5 L4: Suspended Providers Tab */}
          <TabsContent value="suspended">
            {filteredSuspended.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600">No Suspended Providers</h3>
                  <p className="text-gray-500">All providers are in good standing</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Suspended providers cannot receive new leads or offers. Click to reinstate after payment.
                  </p>
                </div>

                {filteredSuspended.map(provider => {
                  const providerType = PROVIDER_TYPES[provider.type] || PROVIDER_TYPES[provider.providerType];
                  return (
                    <Card 
                      key={provider.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-red-500"
                      onClick={() => handleSelectProvider(provider)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-4xl opacity-50">{providerType?.icon || '🏥'}</div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-600">
                                  {provider.legalInfo?.legalName || provider.legalInfo?.tradeName}
                                </h3>
                              </div>
                              <p className="text-sm text-gray-500">
                                {providerType?.name} • {provider.geolocation?.city}, {provider.geolocation?.country}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  SUSPENDED
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {provider.suspensionReason || 'Payment/validity issue'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Suspended On</p>
                            <p className="font-medium text-red-600">
                              {provider.suspendedAt ? formatDateLocale(provider.suspendedAt) : '-'}
                            </p>
                            <Button variant="outline" size="sm" className="mt-2">
                              <RefreshCw className="h-4 w-4 mr-1" /> Reinstate
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* 3C — Identity Changes review tab */}
          <TabsContent value="identity">
            {amendments.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600">All Caught Up!</h3>
                  <p className="text-gray-500" data-testid="identity-changes-empty">
                    {t('treatxAdmin.identityAmendments.noPending')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="identity-changes-list">
                {amendments.map((amd) => {
                  const brandChanged = !!amd.proposed_brand_name && amd.proposed_brand_name !== amd.current_brand_name;
                  const legalChanged = !!amd.proposed_invoice_legal_entity_name && amd.proposed_invoice_legal_entity_name !== amd.current_invoice_legal_entity_name;
                  const name = amd.clinic_brand_name || amd.current_brand_name || amd.clinic_legal_entity_name || amd.clinic_id;
                  return (
                    <Card
                      key={amd.id}
                      className="hover:shadow-md transition-shadow cursor-pointer border-2 hover:border-purple-300"
                      onClick={() => setSelectedAmendment(amd)}
                      data-testid={`identity-change-card-${amd.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="rounded-full bg-purple-100 p-3">
                              <Building2 className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{name}</h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge className="bg-purple-100 text-purple-700 text-xs gap-1">
                                  <Info className="h-3 w-3" />
                                  {t('treatxAdmin.identityAmendments.identityChange')}
                                </Badge>
                                {brandChanged && (
                                  <Badge variant="outline" className="text-xs">{t('treatxAdmin.identityAmendments.publicBrand')}</Badge>
                                )}
                                {legalChanged && (
                                  <Badge variant="outline" className="text-xs">{t('treatxAdmin.identityAmendments.legalName')}</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-gray-500">{t('treatxAdmin.identityAmendments.requestedOn')}</p>
                              <p className="font-medium">{amd.requested_at ? formatDateLocale(amd.requested_at) : '-'}</p>
                            </div>
                            <Button variant="outline">
                              {t('treatxAdmin.identityAmendments.reviewAction')} <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* 3C — Identity Change review dialog */}
      <IdentityAmendmentReviewDialog
        amendment={selectedAmendment}
        open={!!selectedAmendment}
        onOpenChange={(v) => { if (!v) setSelectedAmendment(null); }}
        onReviewed={fetchAmendments}
      />

      {/* Provider Detail Drawer */}
      {selectedProvider && (
        <ProviderDetailDrawer
          provider={selectedProvider}
          onClose={() => setSelectedProvider(null)}
          onApprove={approveApplication}
          onReject={rejectApplication}
          onSuspend={suspendProvider}
          onReinstate={reinstateProvider}
          certifications={allCertifications}
          onApproveCertification={handleApproveCertification}
          onRejectCertification={handleRejectCertification}
        />
      )}
    </div>
  );
};

export default ProviderValidationPanel;